import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Calendar, Clock, MapPin, Phone, Users, Plane, Timer, Info, ChevronUp, ChevronDown, Check, Plus, X, Repeat, Search } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import { createBooking } from '../api';
import { getVehicleImage } from '../utils/vehicleImages';
import { getVehicleCategory, getEffectiveMinHours } from '../data/fleetData';

const API = process.env.REACT_APP_BACKEND_URL;

const BookingNew = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const vehicleParam = searchParams.get('vehicle') || '';
  const serviceParam = searchParams.get('service') || '';
  const pickupParam = searchParams.get('pickup') || '';
  const dropoffParam = searchParams.get('dropoff') || '';
  const hoursParam = searchParams.get('hours') || '';

  // ACE Fleet — fetched from /api/vehicle-tiers (editable in Command Center → Vehicle Tiers)
  const [vehicleOptions, setVehicleOptions] = useState([]);
  const [tiersLoading, setTiersLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/vehicle-tiers`);
        const d = await r.json();
        if (cancelled) return;
        const CATEGORY_ORDER = ['concierge_driven', 'group_driven', 'exotic_driven'];
        const opts = CATEGORY_ORDER
          .filter(c => d.categories?.[c])
          .map(c => ({
            group: d.categories[c].label,
            category: c.replace('_driven', ''),
            items: (d.categories[c].tiers || []).map(t => ({
              value: t.tier_id,
              label: t.tier_name,
              sub: (t.models || []).join(' · ') || t.tier_name,
              pax: t.pax,
              rate: t.hourly_rate,
              type: t.tier_name,
              image_url: t.image_url,
              models: t.models || [],
              min_hours: t.min_hours,
            })),
          }));
        setVehicleOptions(opts);
      } catch (e) {
        console.error('Failed to load vehicle tiers:', e);
      }
      if (!cancelled) setTiersLoading(false);
    })();
    return () => { cancelled = true; };
  }, []);

  const allVehicles = vehicleOptions.flatMap(g => g.items);

  const serviceTypes = [
    { value: 'airport-transfer', label: 'Airport Transfer' },
    { value: 'point-to-point', label: 'Point-to-Point Transfer' },
    { value: 'hourly', label: 'Hourly Charter' },
    { value: 'event-wedding', label: 'Event / Wedding' },
    { value: 'corporate', label: 'Corporate / B2B' },
    { value: 'nightlife', label: 'Nightlife / Special Occasion' },
  ];

  const occasionOptions = [
    'Airport Pickup/Drop-off', 'Corporate Meeting', 'Wedding', 'Prom / Homecoming',
    'Bachelor / Bachelorette', 'Wine Tour', 'Concert / Sporting Event', 'Anniversary / Birthday',
    'Date Night', 'City Tour', 'Content Creation / Music Video', 'Funeral / Memorial', 'Other'
  ];

  // Service-specific note templates
  const serviceTemplates = {
    'airport-transfer': 'Flight info, terminal preference, luggage count, meet & greet signage...',
    'event-wedding': 'Venue name, event timeline, decor preferences, bridal party size...',
    'corporate': 'Company name, billing reference / PO#, attendees, NDAs...',
    'nightlife': 'Itinerary stops, VIP reservations, bottle service needs...',
    'hourly': 'Destinations, schedule flexibility, refreshment requests...',
    'point-to-point': 'Preferred route, stops along the way, timing flexibility...',
  };

  const hourOptions = [];
  for (let h = 1; h <= 12; h++) hourOptions.push(h);

  const getServiceTypeFromParam = (param) => {
    const mapping = { 'Airport Transfer': 'airport-transfer', 'Point-to-Point': 'point-to-point', 'Hourly Charter': 'hourly', 'Event / Wedding': 'event-wedding', 'Corporate / B2B': 'corporate' };
    return mapping[param] || param.toLowerCase().replace(/\s+/g, '-') || '';
  };

  const [formData, setFormData] = useState({
    firstName: '', lastName: '', email: '', phone: '',
    serviceType: getServiceTypeFromParam(serviceParam),
    vehicle: vehicleParam,
    pickupDate: '', pickupTime: '', dropoffTime: '',
    pickupLocation: pickupParam, dropoffLocation: dropoffParam,
    passengers: '', hours: hoursParam || '3',
    flightNumber: '', airlineName: '',
    occasion: '', returnTrip: 'no',
    returnDate: '', returnTime: '', returnPickup: '',
    specialRequests: '',
    recurring: '',
  });
  const [isProcessing, setIsProcessing] = useState(false);
  const [step, setStep] = useState(1);
  const [showEstimate, setShowEstimate] = useState(false);
  const [stops, setStops] = useState([]);
  const [autoFilled, setAutoFilled] = useState(false);
  const [hoursNote, setHoursNote] = useState('');
  const [cart, setCart] = useState([]); // multi-vehicle cart (tier values)

  useEffect(() => {
    if (formData.vehicle && cart.length === 0 && allVehicles.some(v => v.value === formData.vehicle)) {
      setCart([formData.vehicle]);
    }
  }, [formData.vehicle, allVehicles.length]);

  const toggleVehicle = (val) => {
    setCart(prev => {
      const next = prev.includes(val) ? prev.filter(x => x !== val) : [...prev, val];
      setFormData(p => ({ ...p, vehicle: next[0] || '' }));
      return next;
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Auto-fill repeat client when phone or email is entered
  const lookupClient = useCallback(async (phone, email) => {
    if (autoFilled) return;
    const params = phone ? `phone=${encodeURIComponent(phone)}` : `email=${encodeURIComponent(email)}`;
    try {
      const res = await fetch(`${API}/api/clients/lookup?${params}`);
      const data = await res.json();
      if (data.found && data.client) {
        const c = data.client;
        setFormData(prev => ({
          ...prev,
          firstName: prev.firstName || c.firstName || '',
          lastName: prev.lastName || c.lastName || '',
          email: prev.email || c.email || '',
          phone: prev.phone || c.phone || '',
        }));
        setAutoFilled(true);
        toast({ title: "Welcome back!", description: `${c.firstName} ${c.lastName} — details auto-filled${c.vip ? ' (VIP)' : ''}.` });
      }
    } catch {}
  }, [autoFilled]);

  useEffect(() => {
    const phone = formData.phone?.replace(/\D/g, '');
    if (phone && phone.length >= 10 && !autoFilled) {
      const t = setTimeout(() => lookupClient(formData.phone, ''), 500);
      return () => clearTimeout(t);
    }
  }, [formData.phone, autoFilled, lookupClient]);

  useEffect(() => {
    if (formData.email && formData.email.includes('@') && !autoFilled) {
      const t = setTimeout(() => lookupClient('', formData.email), 500);
      return () => clearTimeout(t);
    }
  }, [formData.email, autoFilled, lookupClient]);

  const selectedVehicle = allVehicles.find(v => v.value === formData.vehicle);
  const cartVehicles = cart.map(val => allVehicles.find(x => x.value === val)).filter(Boolean);
  const vehicleCategory = formData.vehicle ? getVehicleCategory(formData.vehicle) : 'concierge';
  const minHours = getEffectiveMinHours(vehicleCategory, formData.serviceType);
  const isAirportTransfer = formData.serviceType === 'airport-transfer';
  const hours = Math.max(minHours, parseInt(formData.hours) || minHours);
  const estimatedTotal = cartVehicles.length > 0 ? cartVehicles.reduce((s, v) => s + v.rate * hours, 0) : (selectedVehicle ? selectedVehicle.rate * hours : null);

  // Auto-adjust hours when vehicle or service type changes
  useEffect(() => {
    if (formData.vehicle) {
      const cat = getVehicleCategory(formData.vehicle);
      const effMin = getEffectiveMinHours(cat, formData.serviceType);
      const currentHours = parseInt(formData.hours) || 0;
      if (currentHours < effMin) {
        setFormData(prev => ({ ...prev, hours: String(effMin) }));
      }
    }
  }, [formData.vehicle, formData.serviceType]);

  // Auto-calculate hours from pickup & drop-off times
  useEffect(() => {
    if (formData.pickupTime && formData.dropoffTime) {
      const [ph, pm] = formData.pickupTime.split(':').map(Number);
      const [dh, dm] = formData.dropoffTime.split(':').map(Number);
      let pickupMins = ph * 60 + pm;
      let dropoffMins = dh * 60 + dm;
      // Overnight: if drop-off is earlier, assume next day
      if (dropoffMins <= pickupMins) dropoffMins += 24 * 60;
      const diffHours = Math.ceil((dropoffMins - pickupMins) / 60);
      const cat = formData.vehicle ? getVehicleCategory(formData.vehicle) : 'concierge';
      const effMin = getEffectiveMinHours(cat, formData.serviceType);
      if (diffHours < effMin) {
        setFormData(prev => ({ ...prev, hours: String(effMin) }));
        setHoursNote(`${effMin}hr minimum applied (calculated ${diffHours}hr)`);
      } else if (diffHours <= 12) {
        setFormData(prev => ({ ...prev, hours: String(diffHours) }));
        setHoursNote(`Auto-calculated from times: ${diffHours}hr`);
      } else {
        setFormData(prev => ({ ...prev, hours: 'custom' }));
        setHoursNote(`${diffHours}hrs calculated — Contact ACE for 13+ hr bookings`);
      }
    } else {
      setHoursNote('');
    }
  }, [formData.pickupTime, formData.dropoffTime, formData.vehicle, formData.serviceType]);

  const step1Valid = formData.serviceType && formData.vehicle && formData.hours;
  const step2Valid = formData.pickupDate && formData.pickupTime && formData.pickupLocation;
  const step3Valid = formData.firstName && formData.lastName && formData.email && formData.phone;

  const addStop = () => setStops(prev => [...prev, { location: '', notes: '' }]);
  const removeStop = (i) => setStops(prev => prev.filter((_, idx) => idx !== i));
  const updateStop = (i, field, value) => setStops(prev => prev.map((s, idx) => idx === i ? { ...s, [field]: value } : s));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.serviceType || !formData.vehicle) {
      toast({ title: "Missing Info", description: "Please select a service type and vehicle.", variant: "destructive" });
      setStep(1);
      return;
    }
    if (!formData.pickupDate || !formData.pickupTime || !formData.pickupLocation) {
      toast({ title: "Missing Info", description: "Please fill in the date, time, and pickup location.", variant: "destructive" });
      setStep(2);
      return;
    }
    if (!formData.firstName || !formData.lastName || !formData.email || !formData.phone) {
      toast({ title: "Missing Info", description: "Please fill in your contact information.", variant: "destructive" });
      setStep(3);
      return;
    }

    setIsProcessing(true);
    try {
      const totalPrice = estimatedTotal || 300;
      const details = [];
      if (formData.occasion) details.push(`Occasion: ${formData.occasion}`);
      if (formData.flightNumber) details.push(`Flight: ${formData.airlineName ? formData.airlineName + ' ' : ''}${formData.flightNumber}`);
      if (formData.returnTrip === 'yes') details.push(`Return Trip: ${formData.returnDate} at ${formData.returnTime} from ${formData.returnPickup || formData.dropoffLocation || 'TBD'}`);
      if (formData.specialRequests) details.push(formData.specialRequests);

      // Safe date parsing
      let pickupDateISO;
      try {
        const d = new Date(formData.pickupDate);
        if (isNaN(d.getTime())) throw new Error('Invalid date');
        pickupDateISO = d.toISOString();
      } catch {
        pickupDateISO = new Date().toISOString();
      }

      const bookingData = {
        vehicleId: formData.vehicle || null,
        vehicleName: cartVehicles.length > 1 ? cartVehicles.map(v => v.label).join(' + ') : (selectedVehicle?.label || ''),
        vehicleRate: cartVehicles.length > 0 ? cartVehicles.reduce((s, v) => s + v.rate, 0) : (selectedVehicle?.rate || 0),
        vehicles: cartVehicles.map(v => ({ tier_id: v.value, label: v.label, rate: v.rate, hours, subtotal: v.rate * hours, pax: v.pax })),
        serviceType: formData.serviceType,
        pickupLocation: formData.pickupLocation,
        dropoffLocation: formData.dropoffLocation || formData.pickupLocation,
        pickupDate: pickupDateISO,
        pickupTime: formData.pickupTime,
        dropoffTime: formData.dropoffTime || null,
        hours, totalPrice,
        specialRequests: details.join(' | '),
        customerInfo: { firstName: formData.firstName, lastName: formData.lastName, email: formData.email, phone: formData.phone },
        status: 'pending',
        trip_status: 'quoted',
        deposit_required: false,
        stops: stops.length > 0 ? stops.filter(s => s.location) : null,
        recurring: formData.recurring || null,
      };

      await createBooking(bookingData);
      navigate('/booking/success?no_payment=true');
    } catch (error) {
      setIsProcessing(false);
      toast({ title: "Booking Request Failed", description: error.response?.data?.detail || error.message || "There was an error. Please try again or call us directly at (678) 846-3009.", variant: "destructive" });
    }
  };

  return (
    <div className="min-h-screen bg-black text-white pb-24 sm:pb-0" data-testid="booking-page">
      {/* Compact Hero */}
      <div className="bg-black text-white py-10 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 text-center">
          <p className="text-xs sm:text-sm text-red-500 font-semibold tracking-widest mb-2 sm:mb-4">RESERVATIONS</p>
          <h1 className="text-2xl sm:text-4xl lg:text-5xl font-bold mb-2 sm:mb-4" data-testid="booking-hero-title">
            Book Your <em className="text-red-500 not-italic">ACE Experience</em>
          </h1>
          <p className="text-sm sm:text-base text-gray-400 max-w-2xl mx-auto">
            Select your service, choose your vehicle, and submit your reservation.
          </p>
        </div>
      </div>

      {/* Mobile Step Indicator */}
      <div className="sm:hidden sticky top-0 z-20 bg-black border-b border-gray-800 px-4 py-3">
        <div className="flex items-center justify-between">
          {[{ n: 1, l: 'Service' }, { n: 2, l: 'When & Where' }, { n: 3, l: 'Contact' }].map((s) => (
            <button key={s.n} onClick={() => setStep(s.n)}
              className={`flex items-center gap-1.5 text-xs font-semibold transition-colors ${step === s.n ? 'text-red-500' : step > s.n ? 'text-emerald-500' : 'text-gray-500'}`}
              data-testid={`step-${s.n}-btn`}>
              <span className={`h-6 w-6 rounded-full flex items-center justify-center text-[10px] font-bold
                ${step === s.n ? 'bg-red-600 text-white' : step > s.n ? 'bg-emerald-950 text-emerald-400' : 'bg-gray-900 text-gray-500'}`}>
                {step > s.n ? <Check className="h-3 w-3" /> : s.n}
              </span>
              {s.l}
            </button>
          ))}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-12">
        <div className="grid lg:grid-cols-3 gap-6 sm:gap-10">
          <div className="lg:col-span-2">
            <Card className="p-4 sm:p-8 bg-gray-950 border-gray-800">
              <h2 className="text-xl sm:text-2xl font-bold text-white mb-1">Request a Booking</h2>
              <p className="text-xs sm:text-sm text-gray-500 mb-4 sm:mb-6">Minimums vary by category: Concierge 3hr, Group 4hr, Exotic 3hr. Point-to-point transfers have no minimum.</p>

              <form onSubmit={handleSubmit} className="space-y-5">
                {/* STEP 1: Service Details */}
                <div className={`${step !== 1 ? 'hidden sm:block' : ''}`} data-testid="booking-step-1">
                  <h3 className="text-xs sm:text-sm font-bold text-red-600 tracking-wider uppercase mb-3 sm:mb-4">Service Details</h3>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-3 sm:mb-4">
                    <div>
                      <Label className="text-gray-300 mb-1.5 block text-sm">Service Type *</Label>
                      <Select value={formData.serviceType} onValueChange={(v) => setFormData(p => ({ ...p, serviceType: v }))}>
                        <SelectTrigger className="bg-gray-900 border-gray-700 text-white placeholder:text-gray-500 h-11 sm:h-9 text-sm" data-testid="booking-service-select"><SelectValue placeholder="Select a service..." /></SelectTrigger>
                        <SelectContent className="bg-gray-900 border-gray-700 text-white">{serviceTypes.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-gray-300 mb-1.5 block text-sm">Occasion</Label>
                      <Select value={formData.occasion} onValueChange={(v) => setFormData(p => ({ ...p, occasion: v }))}>
                        <SelectTrigger className="bg-gray-900 border-gray-700 text-white placeholder:text-gray-500 h-11 sm:h-9 text-sm" data-testid="booking-occasion"><SelectValue placeholder="What's the occasion?" /></SelectTrigger>
                        <SelectContent className="bg-gray-900 border-gray-700 text-white">{occasionOptions.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Vehicle Selection — Mobile-friendly cards */}
                  <div className="mb-3 sm:mb-4">
                    <Label className="text-gray-300 mb-1.5 block text-sm">Vehicle *</Label>
                    {tiersLoading && <p className="text-xs text-gray-400 py-4 text-center">Loading vehicle tiers…</p>}
                    {!tiersLoading && (
                      <>
                        {/* Desktop: fleet-gallery-style tier cards */}
                        <div className="hidden sm:block space-y-5" data-testid="desktop-vehicle-select">
                          <p className="text-[11px] text-gray-500 -mb-1">Tap to select — you can add <span className="text-red-400 font-semibold">multiple vehicles</span> to one booking.</p>
                          {vehicleOptions.map(group => (
                            <div key={group.group}>
                              <p className="text-[11px] font-bold text-red-500 tracking-[0.2em] uppercase mb-2.5">{group.group}</p>
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                {group.items.map(v => {
                                  const selected = cart.includes(v.value);
                                  const cartIdx = cart.indexOf(v.value);
                                  return (
                                    <button key={v.value} type="button" onClick={() => toggleVehicle(v.value)}
                                      className={`rounded-xl overflow-hidden border text-left transition-all bg-gray-950 group
                                        ${selected ? 'border-red-600 ring-2 ring-red-600/40' : 'border-gray-800 hover:border-gray-600'}`}
                                      data-testid={`desktop-vehicle-${v.value}`}>
                                      <div className="aspect-[4/3] bg-gray-900 overflow-hidden relative">
                                        {v.image_url ? (
                                          <img src={v.image_url} alt={v.label} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                                        ) : (
                                          <div className="w-full h-full bg-gray-900" />
                                        )}
                                        {selected && (
                                          <div className="absolute top-2 right-2 h-6 w-6 bg-red-600 rounded-full flex items-center justify-center shadow text-[10px] font-black text-white">{cart.length > 1 ? cartIdx + 1 : <Check className="h-3.5 w-3.5 text-white" />}</div>
                                        )}
                                        <span className="absolute bottom-2 left-2 text-[9px] font-bold bg-black/70 backdrop-blur-sm text-gray-300 px-2 py-0.5 rounded-full">{v.pax} pax</span>
                                      </div>
                                      <div className="p-2.5">
                                        <p className={`text-sm font-bold truncate transition-colors ${selected ? 'text-red-500' : 'text-white group-hover:text-red-500'}`}>{v.label}</p>
                                        {(v.models || []).length > 0 && (
                                          <p className="text-[10px] text-gray-500 truncate" title={(v.models || []).join(', ')}>{(v.models || []).slice(0, 2).join(' · ')}{(v.models || []).length > 2 ? ` +${v.models.length - 2}` : ''}</p>
                                        )}
                                        <div className="flex items-baseline gap-1 mt-0.5">
                                          <span className="text-base font-black text-white">${v.rate}</span>
                                          <span className="text-[10px] text-gray-500">/hr chauffeured</span>
                                        </div>
                                      </div>
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                          ))}
                        </div>
                    {/* Mobile: 2-column grid per group with thumbnails */}
                    <div className="sm:hidden space-y-5" data-testid="mobile-vehicle-select">
                      {vehicleOptions.map(group => {
                        const minRate = Math.min(...group.items.map(v => v.rate));
                        const maxRate = Math.max(...group.items.map(v => v.rate));
                        const priceRange = minRate === maxRate ? `$${minRate}/hr` : `$${minRate}–$${maxRate}/hr`;
                        return (
                          <div key={group.group}>
                            <div className="flex items-center justify-between mb-2">
                              <p className="text-xs font-bold text-red-500 tracking-wider uppercase">{group.group}</p>
                              <span className="text-[10px] text-gray-500 font-semibold">From {priceRange}</span>
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                              {group.items.map(v => (
                                <button key={v.value} type="button" onClick={() => toggleVehicle(v.value)}
                                  className={`rounded-xl overflow-hidden border text-left transition-all bg-gray-950
                                    ${cart.includes(v.value) ? 'border-red-600 ring-2 ring-red-600/40' : 'border-gray-800'}`}
                                  data-testid={`mobile-vehicle-${v.value}`}>
                                  <div className="h-20 bg-gray-900 overflow-hidden relative">
                                    <img src={v.image_url || getVehicleImage(v.value)} alt={v.sub || v.label} className="w-full h-full object-cover" loading="lazy" />
                                    {cart.includes(v.value) && (
                                      <div className="absolute top-1.5 right-1.5 h-5 w-5 bg-red-600 rounded-full flex items-center justify-center shadow text-[9px] font-black text-white">
                                        {cart.length > 1 ? cart.indexOf(v.value) + 1 : <Check className="h-3 w-3 text-white" />}
                                      </div>
                                    )}
                                    <span className="absolute bottom-1 left-1 text-[8px] font-bold bg-black/70 backdrop-blur-sm text-gray-300 px-1.5 py-0.5 rounded-full">
                                      {v.pax} pax
                                    </span>
                                  </div>
                                  <div className="p-2">
                                    <p className="text-[11px] font-bold text-white truncate">{v.label}</p>
                                    {v.sub && <p className="text-[9px] text-gray-500 truncate">{v.sub}</p>}
                                    <span className="text-sm font-black text-white">${v.rate}</span>
                                    <span className="text-[9px] text-gray-500">/hr</span>
                                  </div>
                                </button>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                      </>
                    )}
                  </div>

                  {/* Hours & Passengers */}
                  <div className="grid grid-cols-2 gap-3 sm:gap-4">
                    <div>
                      <Label className="text-gray-300 mb-1.5 block text-sm"><Timer className="inline h-3.5 w-3.5 mr-1" />Hours *</Label>
                      <Select value={formData.hours} onValueChange={(v) => { setFormData(p => ({ ...p, hours: v })); setHoursNote(''); }}>
                        <SelectTrigger className="bg-gray-900 border-gray-700 text-white placeholder:text-gray-500 h-11 sm:h-9 text-sm" data-testid="booking-hours"><SelectValue placeholder={`${minHours} hrs`} /></SelectTrigger>
                        <SelectContent className="bg-gray-900 border-gray-700 text-white">
                          {hourOptions.filter(h => h >= minHours).map(h => <SelectItem key={h} value={String(h)}>{h} hrs{h === minHours ? ' (min)' : ''} {selectedVehicle ? `— $${selectedVehicle.rate * h}` : ''}</SelectItem>)}
                          <SelectItem value="custom">13+ hrs — Contact ACE</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-gray-300 mb-1.5 block text-sm"><Users className="inline h-3.5 w-3.5 mr-1" />Passengers *</Label>
                      <Select value={formData.passengers} onValueChange={(v) => setFormData(p => ({ ...p, passengers: v }))}>
                        <SelectTrigger className="bg-gray-900 border-gray-700 text-white placeholder:text-gray-500 h-11 sm:h-9 text-sm" data-testid="booking-passengers"><SelectValue placeholder="Select..." /></SelectTrigger>
                        <SelectContent className="bg-gray-900 border-gray-700 text-white">{['1', '2', '3', '4', '5', '6', '7-10', '11-14', '15-20', '20-27', '28-37'].map(p => <SelectItem key={p} value={p}>{p} pax</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  </div>

                  {hoursNote && (
                    <p className="text-xs text-amber-600 font-medium mt-1.5" data-testid="hours-auto-note">
                      <Clock className="inline h-3 w-3 mr-1" />{hoursNote}
                    </p>
                  )}

                  {/* Mobile: Next Step button */}
                  <div className="sm:hidden mt-4">
                    <Button type="button" onClick={() => setStep(2)} disabled={!step1Valid} className="w-full h-12 bg-red-600 hover:bg-red-700 text-white" data-testid="next-step-2">
                      Next: When & Where
                    </Button>
                  </div>
                </div>

                {/* Desktop separator */}
                <hr className="border-gray-800 hidden sm:block" />

                {/* STEP 2: Date/Time/Location */}
                <div className={`${step !== 2 ? 'hidden sm:block' : ''}`} data-testid="booking-step-2">
                  <h3 className="text-xs sm:text-sm font-bold text-red-600 tracking-wider uppercase mb-3 sm:mb-4">Date, Time & Location</h3>

                  {/* Date + Pickup Time + Drop-off Time */}
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4 mb-3 sm:mb-4">
                    <div>
                      <Label className="text-gray-300 mb-1.5 block text-sm"><Calendar className="inline h-3.5 w-3.5 mr-1" />Date *</Label>
                      <Input type="date" name="pickupDate" value={formData.pickupDate} onChange={handleChange} required
                        min={new Date().toISOString().split('T')[0]} className="bg-gray-900 border-gray-700 text-white placeholder:text-gray-500 h-11 sm:h-9 text-sm" data-testid="booking-date" />
                    </div>
                    <div>
                      <Label className="text-gray-300 mb-1.5 block text-sm"><Clock className="inline h-3.5 w-3.5 mr-1" />Pickup *</Label>
                      <Input type="time" name="pickupTime" value={formData.pickupTime} onChange={handleChange} required
                        className="bg-gray-900 border-gray-700 text-white placeholder:text-gray-500 h-11 sm:h-9 text-sm" data-testid="booking-pickup-time" />
                    </div>
                    <div className="col-span-2 sm:col-span-1">
                      <Label className="text-gray-300 mb-1.5 block text-sm"><Clock className="inline h-3.5 w-3.5 mr-1" />Drop-off Time</Label>
                      <Input type="time" name="dropoffTime" value={formData.dropoffTime} onChange={handleChange}
                        className="bg-gray-900 border-gray-700 text-white placeholder:text-gray-500 h-11 sm:h-9 text-sm" data-testid="booking-dropoff-time" />
                    </div>
                  </div>

                  <div className="space-y-3 sm:space-y-4 mb-3">
                    <div>
                      <Label className="text-gray-300 mb-1.5 block text-sm"><MapPin className="inline h-3.5 w-3.5 mr-1" />Pickup *</Label>
                      <Input name="pickupLocation" value={formData.pickupLocation} onChange={handleChange} required
                        placeholder="ATL Airport, 123 Main St, Hotel Name..."
                        className="bg-gray-900 border-gray-700 text-white placeholder:text-gray-500 h-11 sm:h-9 text-sm" data-testid="booking-pickup" />
                    </div>
                    <div>
                      <Label className="text-gray-300 mb-1.5 block text-sm"><MapPin className="inline h-3.5 w-3.5 mr-1" />Drop-off</Label>
                      <Input name="dropoffLocation" value={formData.dropoffLocation} onChange={handleChange}
                        placeholder="Four Seasons, Midtown Atlanta..."
                        className="bg-gray-900 border-gray-700 text-white placeholder:text-gray-500 h-11 sm:h-9 text-sm" data-testid="booking-dropoff" />
                    </div>
                  </div>

                  {/* Multi-Stop Itinerary */}
                  {stops.length > 0 && (
                    <div className="border-l-2 border-red-200 pl-3 mb-3 space-y-2" data-testid="multi-stop-section">
                      <p className="text-xs font-bold text-red-600 tracking-wider uppercase">Additional Stops</p>
                      {stops.map((s, i) => (
                        <div key={i} className="flex gap-2 items-start">
                          <div className="flex-1 space-y-1">
                            <Input value={s.location} onChange={e => updateStop(i, 'location', e.target.value)}
                              placeholder={`Stop ${i + 1} address...`} className="bg-gray-900 border-gray-700 text-white placeholder:text-gray-500 h-11 sm:h-9 text-sm" data-testid={`stop-${i}-location`} />
                            <Input value={s.notes} onChange={e => updateStop(i, 'notes', e.target.value)}
                              placeholder="Wait time, notes..." className="bg-gray-900 border-gray-700 text-white placeholder:text-gray-500 h-9 sm:h-8 text-xs" data-testid={`stop-${i}-notes`} />
                          </div>
                          <button type="button" onClick={() => removeStop(i)} className="mt-1 text-gray-400 hover:text-red-500"><X className="h-4 w-4" /></button>
                        </div>
                      ))}
                    </div>
                  )}
                  <button type="button" onClick={addStop} className="flex items-center gap-1 text-xs text-red-600 font-semibold hover:text-red-700 mb-3" data-testid="add-stop-btn">
                    <Plus className="h-3 w-3" /> Add a Stop
                  </button>

                  {/* Airport Details */}
                  {isAirportTransfer && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 sm:p-4 mb-3" data-testid="airport-details-section">
                      <h3 className="text-xs font-bold text-blue-800 uppercase mb-2 flex items-center gap-1.5">
                        <Plane className="h-3.5 w-3.5" /> Flight Info
                      </h3>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-gray-400 mb-1 block text-xs">Airline</Label>
                          <Input name="airlineName" value={formData.airlineName} onChange={handleChange}
                            placeholder="Delta, United..." className="border-blue-200 bg-white h-11 sm:h-9 text-sm" data-testid="booking-airline" />
                        </div>
                        <div>
                          <Label className="text-gray-400 mb-1 block text-xs">Flight #</Label>
                          <Input name="flightNumber" value={formData.flightNumber} onChange={handleChange}
                            placeholder="DL 1234" className="bg-gray-900 border-blue-900 text-white placeholder:text-gray-500 h-11 sm:h-9 text-sm" data-testid="booking-flight-number" />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Return Trip */}
                  <div className="mb-3">
                    <p className="text-xs font-bold text-red-600 tracking-wider uppercase mb-2">Return Trip</p>
                    <div className="flex gap-3 mb-2">
                      {[{ v: 'no', l: 'One-way' }, { v: 'yes', l: 'Round-trip' }].map(o => (
                        <button key={o.v} type="button" onClick={() => setFormData(p => ({ ...p, returnTrip: o.v }))}
                          className={`flex-1 h-11 sm:h-9 rounded-lg border-2 text-sm font-semibold transition-all
                            ${formData.returnTrip === o.v ? 'border-red-600 bg-red-950/40 text-red-400' : 'border-gray-700 text-gray-400'}`}
                          data-testid={`booking-return-${o.v}`}>{o.l}
                        </button>
                      ))}
                    </div>
                    {formData.returnTrip === 'yes' && (
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 border-l-2 border-red-200 pl-3" data-testid="return-trip-details">
                        <Input type="date" name="returnDate" value={formData.returnDate} onChange={handleChange}
                          min={formData.pickupDate || new Date().toISOString().split('T')[0]}
                          className="bg-gray-900 border-gray-700 text-white placeholder:text-gray-500 h-11 sm:h-9 text-sm" placeholder="Return Date" data-testid="booking-return-date" />
                        <Input type="time" name="returnTime" value={formData.returnTime} onChange={handleChange}
                          className="bg-gray-900 border-gray-700 text-white placeholder:text-gray-500 h-11 sm:h-9 text-sm" data-testid="booking-return-time" />
                        <Input name="returnPickup" value={formData.returnPickup} onChange={handleChange}
                          placeholder="Return pickup" className="bg-gray-900 border-gray-700 text-white placeholder:text-gray-500 h-11 sm:h-9 text-sm col-span-2 sm:col-span-1" data-testid="booking-return-pickup" />
                      </div>
                    )}
                  </div>

                  {/* Recurring Booking */}
                  <div className="mb-3">
                    <p className="text-xs font-bold text-red-600 tracking-wider uppercase mb-2"><Repeat className="inline h-3 w-3 mr-1" />Recurring</p>
                    <div className="flex gap-2 flex-wrap">
                      {[{ v: '', l: 'One-time' }, { v: 'daily', l: 'Daily' }, { v: 'weekly', l: 'Weekly' }, { v: 'biweekly', l: 'Bi-weekly' }, { v: 'monthly', l: 'Monthly' }].map(o => (
                        <button key={o.v} type="button" onClick={() => setFormData(p => ({ ...p, recurring: o.v }))}
                          className={`px-3 py-1.5 rounded-full border text-xs font-semibold transition-all
                            ${formData.recurring === o.v ? 'border-red-600 bg-red-950/40 text-red-400' : 'border-gray-700 text-gray-400 hover:bg-gray-900'}`}
                          data-testid={`recurring-${o.v || 'none'}`}>{o.l}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Special Requests with template hint */}
                  <div>
                    <Label className="text-gray-300 mb-1.5 block text-sm">Special Requests</Label>
                    <Textarea name="specialRequests" value={formData.specialRequests} onChange={handleChange} rows={2}
                      placeholder={serviceTemplates[formData.serviceType] || 'Child seats, accessibility, signage, beverages...'}
                      className="bg-gray-900 border-gray-700 text-white placeholder:text-gray-500 text-sm" data-testid="booking-requests" />
                  </div>

                  <div className="sm:hidden mt-4 flex gap-2">
                    <Button type="button" onClick={() => setStep(1)} variant="outline" className="h-12 px-6 border-gray-700 text-gray-300 hover:bg-gray-900">Back</Button>
                    <Button type="button" onClick={() => setStep(3)} disabled={!step2Valid} className="flex-1 h-12 bg-red-600 hover:bg-red-700 text-white" data-testid="next-step-3">
                      Next: Your Info
                    </Button>
                  </div>
                </div>

                <hr className="border-gray-800 hidden sm:block" />

                {/* STEP 3: Contact Info */}
                <div className={`${step !== 3 ? 'hidden sm:block' : ''}`} data-testid="booking-step-3">
                  <h3 className="text-xs sm:text-sm font-bold text-red-600 tracking-wider uppercase mb-3 sm:mb-4">Your Information</h3>
                  {autoFilled && (
                    <div className="bg-emerald-950/40 border border-emerald-900 rounded-lg p-2 mb-3 flex items-center gap-2 text-xs text-emerald-400" data-testid="autofill-badge">
                      <Search className="h-3.5 w-3.5" /> Welcome back! Your details were auto-filled from a previous booking.
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-3 sm:gap-4 mb-3 sm:mb-4">
                    <div>
                      <Label className="text-gray-300 mb-1.5 block text-sm">First Name *</Label>
                      <Input name="firstName" value={formData.firstName} onChange={handleChange} required
                        className="bg-gray-900 border-gray-700 text-white placeholder:text-gray-500 h-11 sm:h-9 text-sm" data-testid="booking-first-name" />
                    </div>
                    <div>
                      <Label className="text-gray-300 mb-1.5 block text-sm">Last Name *</Label>
                      <Input name="lastName" value={formData.lastName} onChange={handleChange} required
                        className="bg-gray-900 border-gray-700 text-white placeholder:text-gray-500 h-11 sm:h-9 text-sm" data-testid="booking-last-name" />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                    <div>
                      <Label className="text-gray-300 mb-1.5 block text-sm">Email *</Label>
                      <Input type="email" name="email" value={formData.email} onChange={handleChange} required
                        className="bg-gray-900 border-gray-700 text-white placeholder:text-gray-500 h-11 sm:h-9 text-sm" data-testid="booking-email" />
                    </div>
                    <div>
                      <Label className="text-gray-300 mb-1.5 block text-sm"><Phone className="inline h-3.5 w-3.5 mr-1" />Phone *</Label>
                      <Input type="tel" name="phone" value={formData.phone} onChange={handleChange} required
                        placeholder="(404) 555-1234" className="bg-gray-900 border-gray-700 text-white placeholder:text-gray-500 h-11 sm:h-9 text-sm" data-testid="booking-phone" />
                    </div>
                  </div>

                  <div className="sm:hidden mt-4 flex gap-2">
                    <Button type="button" onClick={() => setStep(2)} variant="outline" className="h-12 px-6 border-gray-700 text-gray-300 hover:bg-gray-900">Back</Button>
                    <Button type="submit" disabled={isProcessing || !(step1Valid && step2Valid && step3Valid)}
                      className="flex-1 h-12 bg-red-600 hover:bg-red-700 text-white font-semibold" data-testid="submit-booking-btn">
                      {isProcessing ? 'Processing...' : 'Request Booking'}
                    </Button>
                  </div>
                </div>

                {/* Desktop Submit */}
                <div className="hidden sm:block">
                  <Button type="submit" disabled={isProcessing || !(step1Valid && step2Valid && step3Valid)}
                    className="w-full h-14 bg-red-600 hover:bg-red-700 text-white font-semibold text-lg disabled:opacity-50" data-testid="submit-booking-btn-desktop">
                    {isProcessing ? 'Processing...' : 'Request Booking'}
                  </Button>
                  <p className="text-xs text-gray-500 text-center mt-2">No deposit required upfront. ACE personally confirms every booking within one business day.</p>
                </div>
              </form>
            </Card>
          </div>

          {/* Sidebar — Desktop */}
          <div className="hidden lg:block space-y-6">
            {selectedVehicle && (
              <Card className="p-6 bg-gray-950 border-2 border-red-600 sticky top-4" data-testid="booking-price-estimate">
                <h3 className="text-lg font-bold text-white mb-3">Booking Estimate</h3>
                <div className="space-y-2 text-sm mb-4">
                  {cartVehicles.length > 1 ? (
                    <div className="space-y-1.5 pb-2 border-b border-gray-800" data-testid="estimate-line-items">
                      {cartVehicles.map((v, i) => (
                        <div key={v.value} className="flex justify-between items-baseline">
                          <span className="text-gray-400 text-xs truncate max-w-[60%]">{i + 1}. {v.label}</span>
                          <span className="text-gray-200 text-xs font-semibold">${v.rate}/hr · ${v.rate * hours}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <>
                      <div className="flex justify-between"><span className="text-gray-400">Vehicle</span><span className="font-semibold text-white text-right text-xs max-w-[60%]">{selectedVehicle.label}</span></div>
                      <div className="flex justify-between"><span className="text-gray-400">Capacity</span><span className="text-gray-200">{selectedVehicle.pax} pax</span></div>
                      <div className="flex justify-between"><span className="text-gray-400">Rate</span><span className="font-semibold text-gray-200">${selectedVehicle.rate}/hr</span></div>
                    </>
                  )}
                  <div className="flex justify-between"><span className="text-gray-400">Hours</span><span className="text-gray-200">{hours} hrs</span></div>
                  {formData.pickupTime && <div className="flex justify-between"><span className="text-gray-400">Pickup</span><span className="text-gray-200">{formData.pickupTime}</span></div>}
                  {formData.dropoffTime && <div className="flex justify-between"><span className="text-gray-400">Drop-off</span><span className="text-gray-200">{formData.dropoffTime}</span></div>}
                  <hr className="border-gray-800" />
                  <div className="flex justify-between pt-1"><span className="font-bold text-white">Estimated Total</span><span className="text-2xl font-bold text-red-500">${estimatedTotal}</span></div>
                </div>
                <p className="text-[11px] text-gray-500">No deposit required upfront. Taxes & gratuity not included.</p>
              </Card>
            )}

            <Card className="p-6 bg-gray-950 border-gray-800" data-testid="booking-important-info">
              <h3 className="text-base font-bold text-white mb-3 flex items-center gap-2"><Info className="h-4 w-4 text-amber-500" /> Important</h3>
              <ul className="space-y-1.5 text-xs text-gray-400">
                <li>Concierge Driven: 3hr min (no min for transfers)</li>
                <li>Group Driven: 4hr minimum</li>
                <li>Exotic Driven: 3hr min, 48-72hr notice</li>
                <li>Airport: 30-min complimentary wait</li>
                <li>20% gratuity not included in rate</li>
                <li>24hr cancellations may incur fee</li>
                <li>No upfront deposit — pay when confirmed</li>
              </ul>
            </Card>

            <Card className="p-6 bg-gray-950 border-gray-800">
              <h3 className="text-base font-bold text-white mb-2">Prefer a Call?</h3>
              <p className="text-xs text-gray-400 mb-3">Book a free Discovery Call and we'll design your experience together.</p>
              <a href="tel:6788463009" className="flex items-center justify-center text-sm text-gray-300 hover:text-red-500 transition-colors">
                <Phone className="h-4 w-4 mr-2" /> (678) 846-3009
              </a>
            </Card>
          </div>
        </div>
      </div>

      {/* Mobile Sticky Bottom Bar */}
      {selectedVehicle && (
        <div className="fixed bottom-0 left-0 right-0 bg-gray-950 border-t-2 border-red-600 px-4 py-3 z-50 sm:hidden shadow-2xl" data-testid="mobile-price-bar">
          <button onClick={() => setShowEstimate(!showEstimate)} className="w-full flex items-center justify-between pr-12">
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-400 truncate max-w-[120px]">{cartVehicles.length > 1 ? `${cartVehicles.length} vehicles` : selectedVehicle.label}</span>
              {showEstimate ? <ChevronDown className="h-4 w-4 text-gray-500" /> : <ChevronUp className="h-4 w-4 text-gray-500" />}
            </div>
            <div className="text-right">
              <p className="text-xs text-gray-400">{cartVehicles.length > 1 ? `${hours}hrs × ${cartVehicles.length} vehicles` : `${hours}hrs @ $${selectedVehicle.rate}/hr`}</p>
              <p className="text-lg font-bold text-red-500">${estimatedTotal}</p>
            </div>
          </button>
          {showEstimate && (
            <div className="mt-3 pt-3 border-t border-gray-800 space-y-1.5 text-xs">
              {cartVehicles.length > 1 ? (
                cartVehicles.map((v, i) => (
                  <div key={v.value} className="flex justify-between"><span className="text-gray-400 truncate max-w-[60%]">{i + 1}. {v.label}</span><span className="font-semibold text-gray-200">${v.rate * hours}</span></div>
                ))
              ) : (
                <>
                  <div className="flex justify-between"><span className="text-gray-400">Vehicle</span><span className="font-semibold text-gray-200">{selectedVehicle.label}</span></div>
                  <div className="flex justify-between"><span className="text-gray-400">Capacity</span><span className="text-gray-200">{selectedVehicle.pax} passengers</span></div>
                  <div className="flex justify-between"><span className="text-gray-400">Rate</span><span className="font-semibold text-gray-200">${selectedVehicle.rate}/hr</span></div>
                </>
              )}
              <div className="flex justify-between"><span className="text-gray-400">Duration</span><span className="text-gray-200">{hours} hours</span></div>
              {formData.pickupTime && <div className="flex justify-between"><span className="text-gray-400">Pickup</span><span className="text-gray-200">{formData.pickupTime}</span></div>}
              {formData.dropoffTime && <div className="flex justify-between"><span className="text-gray-400">Drop-off</span><span className="text-gray-200">{formData.dropoffTime}</span></div>}
              <hr className="border-gray-800" />
              <div className="flex justify-between font-bold"><span className="text-white">Estimated Total</span><span className="text-red-500">${estimatedTotal}</span></div>
              <p className="text-[10px] text-gray-500">No deposit required. Taxes & gratuity not included.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default BookingNew;
